import type { CSSProperties, FC } from 'react'
import type { SceneProps } from '@/components/landing/games/scene-ui'

import { useState } from 'react'
import { cn } from '@/lib/utils'
import { useSequence } from '@/hooks/use-sequence'
import { SceneBadge, SceneStat } from '@/components/landing/games/scene-ui'

type Phase = 'pumping' | 'burst' | 'cashed'

const SEALS = 25
const WEAK = 2

/** Medium risk: two weak seals in twenty five. Rung 23 pays 297x, as in the real game. */
const rungAt = (step: number): number => {
    if (step === 0) return 1
    return (0.99 * SEALS * (SEALS - 1)) / ((SEALS - step) * (SEALS - 1 - step))
}

const PARTICLES = Array.from({ length: 12 }, (_, index) => {
    const angle = (index / 12) * Math.PI * 2
    const distance = index % 2 === 0 ? 84 : 64

    return {
        dx: `${(Math.cos(angle) * distance).toFixed(1)}px`,
        dy: `${(Math.sin(angle) * distance).toFixed(1)}px`
    }
})

const PumpScene: FC<SceneProps> = ({ active }) => {
    const [step, setStep] = useState(4)
    const [phase, setPhase] = useState<Phase>('pumping')

    useSequence(active, async ({ wait }) => {
        setStep(0)
        setPhase('pumping')

        if (!(await wait(800))) return

        const target = 3 + Math.floor(Math.random() * 6)

        for (let current = 0; current < SEALS - WEAK; current++) {
            if (!(await wait(640))) return

            if (Math.random() < WEAK / (SEALS - current)) {
                setPhase('burst')
                await wait(1700)
                return
            }

            setStep(current + 1)

            if (current + 1 >= target) {
                if (!(await wait(450))) return
                setPhase('cashed')
                await wait(1700)
                return
            }
        }
    })

    const scale = 0.55 + Math.min(step, 10) * 0.05

    return (
        <div className='absolute inset-0 flex flex-col px-5 pb-5 pt-14'>
            <div className='relative grid flex-1 place-items-center'>
                {phase === 'burst'
                    ? (
                        <div className='relative size-[150px]'>
                            {PARTICLES.map((particle, index) => (
                                <span
                                    key={index}
                                    className='absolute left-1/2 top-1/2 size-2 animate-pop-particle rounded-full bg-ember'
                                    style={{ '--dx': particle.dx, '--dy': particle.dy } as CSSProperties}
                                />
                            ))}
                            <span className='absolute inset-[28%] animate-ring-out rounded-full border-2 border-danger' />
                        </div>
                    )
                    : (
                        <div
                            className='relative size-[150px] transition-transform duration-500 ease-[cubic-bezier(0.34,1.56,0.64,1)]'
                            style={{ transform: `scale(${scale})` }}
                        >
                            <div className='absolute inset-0 rounded-full bg-[radial-gradient(circle_at_34%_28%,rgba(255,255,255,0.95)_0%,rgba(255,180,145,0.72)_9%,rgba(255,91,31,0.55)_34%,rgba(255,91,31,0.2)_62%,rgba(255,91,31,0.06)_74%)] shadow-[inset_0_0_30px_rgba(255,91,31,0.35),0_0_70px_-12px_rgba(255,91,31,0.75)]' />
                            <div className='absolute inset-[5%] rounded-full border border-white/15' />
                            <div className='absolute left-[22%] top-[15%] h-[17%] w-[26%] -rotate-[30deg] rounded-full bg-white/45 blur-[2px]' />
                            {phase === 'cashed' && <div className='absolute inset-0 animate-ring-out rounded-full border-2 border-ember' />}
                        </div>
                    )}

                <div className='pointer-events-none absolute inset-0 grid place-items-center'>
                    <span
                        className={cn(
                            'font-mono text-2xl font-medium tabular-nums [text-shadow:0_2px_14px_rgba(0,0,0,0.65)]',
                            phase === 'burst' ? 'text-danger line-through' : 'text-bone'
                        )}
                    >
                        ×{rungAt(step).toFixed(2)}
                    </span>
                </div>

                <div aria-hidden='true' className='absolute bottom-0 left-1/2 h-3 w-12 -translate-x-1/2 rounded-t-lg bg-ink-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.08)]' />
            </div>

            <div className='mt-3 flex items-end justify-between gap-2'>
                <SceneStat label='Pumps' value={step} />

                {phase === 'pumping' && <SceneBadge tone='muted'>Next ×{rungAt(step + 1).toFixed(2)}</SceneBadge>}
                {phase === 'cashed' && <SceneBadge tone='ember'>Cashed out</SceneBadge>}
                {phase === 'burst' && <SceneBadge tone='danger'>Burst</SceneBadge>}
            </div>
        </div>
    )
}

export default PumpScene
